import { Component, h } from 'preact'
import TextField from '../text-field'
import { Calendar } from './Calendar'

export interface DateRange {
  from: Date | undefined
  to: Date | undefined
}

interface DatePickerProps {
  dateRange?: boolean
  date?: Date
  startDate?: Date
  endDate?: Date
  flex?: boolean
  onDateChange?: (date: Date) => void
  onDateRangeChange?: (dates: DateRange) => void
}

interface DatePickerState {
  flex?: boolean
  selectedDate?: Date
  selectedStartDate?: Date
  selectedEndDate?: Date
  visibleMonth: number
  visibleYear: number
}

class DatePicker extends Component<DatePickerProps, DatePickerState> {
  private readonly now = new Date()

  constructor (props: DatePickerProps) {
    super(props)

    this.state = {
      flex: props.flex || undefined,
      selectedDate: props.date || undefined,
      selectedStartDate: props.startDate || undefined,
      selectedEndDate: props.endDate || undefined,
      visibleMonth: this.now.getMonth(),
      visibleYear: this.now.getFullYear()
    }

    this.handleChangeMonth = this.handleChangeMonth.bind(this)
    this.handleSelectDate = this.handleSelectDate.bind(this)
    this.handleTextFieldChange = this.handleTextFieldChange.bind(this)
  }

  handleChangeMonth (offset: 1 | -1) {
    const { visibleMonth, visibleYear } = this.state

    let newMonth = visibleMonth + offset
    let newYear = visibleYear

    switch (newMonth) {
      case -1:
        newMonth = 11
        newYear--
        break
      case 12:
        newMonth = 0
        newYear++
        break
    }

    this.setState({
      visibleMonth: newMonth,
      visibleYear: newYear
    })
  }

  handleTextFieldChange (value: string) {
    const dateStrings = value.split(' to ')

    if (dateStrings.length === 1 && !isNaN(Date.parse(dateStrings[0]))) {
      const newStartDate = new Date(dateStrings[0])
      this.setDateRange(newStartDate, undefined)
      return
    }

    if (dateStrings.length === 2 && !isNaN(Date.parse(dateStrings[0])) && !isNaN(Date.parse(dateStrings[1]))) {
      const newStartDate = new Date(dateStrings[0])
      const newEndDate = new Date(dateStrings[1])
      newEndDate.setHours(23, 59, 59, 999)
      this.setDateRange(newStartDate, newEndDate)
      return
    }

    this.setDateRange(undefined, undefined)
  }

  setDateRange (start?: Date, end?: Date) {
    this.setState({
      selectedStartDate: start,
      selectedEndDate: end
    })
    this.props.onDateRangeChange?.({
      from: start,
      to: end
    })
  }

  handleSelectDate (day: number) {
    const { visibleMonth, visibleYear } = this.state
    const selectedDate = new Date(visibleYear, visibleMonth, day)

    if (this.props.dateRange) {
      this.selectDateRangeDate(selectedDate)
    } else {
      this.selectSingleDate(selectedDate)
    }
  }

  selectSingleDate (selectedDate: Date) {
    this.setState({ selectedDate })
    this.props.onDateChange?.(selectedDate)
  }

  selectDateRangeDate (selectedDate: Date) {
    const { selectedStartDate, selectedEndDate } = this.state
    const isStartDate = (Boolean(selectedStartDate) === Boolean(selectedEndDate)) ||
        selectedDate.getTime() <= Number(selectedStartDate?.getTime())

    if (isStartDate) {
      this.setDateRange(selectedDate, undefined)
    } else {
      selectedDate.setHours(23, 59, 59, 999)
      this.setDateRange(selectedStartDate, selectedDate)
    }
  }

  getFormattedInputValue () {
    const { selectedStartDate, selectedEndDate } = this.state
    if (!selectedStartDate) {
      return ''
    }

    if (!selectedEndDate) {
      return this.formatDate(selectedStartDate)
    }

    return `${this.formatDate(selectedStartDate)} to ${this.formatDate(selectedEndDate)}`
  }

  formatDate (date: Date) {
    const month = date.toLocaleString('en-GB', { month: 'short' })
    const day = date.getDate()
    const year = date.getFullYear()
    return `${month} ${day}, ${year}`
  }

  render () {
    const { dateRange } = this.props
    const { selectedDate, selectedStartDate, selectedEndDate, visibleMonth, visibleYear, flex } = this.state
    return (
      <div className={`o-date-picker${flex ? ' o-date-picker-flex' : ''}`}>
        {
          dateRange &&
            <TextField
              id='date-picker'
              ariaLabel='date-picker'
              name='date-picker'
              isControl
              defaultValue={selectedStartDate ? this.getFormattedInputValue() : undefined}
              updateInputValue={this.getFormattedInputValue()}
              onChange={this.handleTextFieldChange}
            />
        }
        <div className={`m-calendar-wrapper${flex ? ' m-calendar-wrapper-flex' : ''}`}>
          <Calendar
            month={visibleMonth}
            year={visibleYear}
            selectedDate={selectedDate}
            selectedStartDate={selectedStartDate}
            selectedEndDate={selectedEndDate}
            today={this.now}
            onChangeMonth={this.handleChangeMonth}
            onSelectDate={this.handleSelectDate}
          />
        </div>
      </div>
    )
  }
}

export default DatePicker
